/**
 * Offline Queue Utility
 * Stores failed POST/PUT requests in IndexedDB and syncs when online.
 */

const DB_NAME = "gms_offline";
const DB_VERSION = 1;
const STORE = "queue";
const MAX_QUEUE = 50;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const d = e.target.result;
      if (!d.objectStoreNames.contains(STORE)) {
        d.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueue(request) {
  const db = await openDB();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);

  // Check queue size limit
  const count = await new Promise((r) => {
    const req = store.count();
    req.onsuccess = () => r(req.result);
  });
  if (count >= MAX_QUEUE) {
    db.close();
    throw new Error("Offline queue full (max 50). Please sync before adding more.");
  }

  const entry = {
    url: request.url,
    method: request.method,
    headers: {
      "Content-Type": request.headers?.["Content-Type"] || "application/json",
    },
    body: request.data,
    timestamp: new Date().toISOString(),
  };

  await new Promise((resolve, reject) => {
    const req = store.add(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  db.close();

  // Try to register background sync
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    const reg = await navigator.serviceWorker.ready;
    await reg.sync.register("sync-entries").catch(() => {});
  }
}

export async function getQueueCount() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE, "readonly");
    const count = await new Promise((r) => {
      const req = tx.objectStore(STORE).count();
      req.onsuccess = () => r(req.result);
    });
    db.close();
    return count;
  } catch {
    return 0;
  }
}

export async function processQueue(apiInstance) {
  let db;
  try {
    db = await openDB();
  } catch {
    return { processed: 0, failed: 0 };
  }
  const tx = db.transaction(STORE, "readonly");
  const entries = await new Promise((r) => {
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => r(req.result);
    req.onerror = () => r([]);
  });
  db.close();

  let processed = 0;
  let failed = 0;

  for (const entry of entries) {
    try {
      // Auth is handled by httpOnly cookies automatically via withCredentials
      await apiInstance({
        url: entry.url,
        method: entry.method,
        data: entry.body,
        headers: entry.headers,
        _isOfflineSync: true,
      });

      // Remove from queue on success
      const delDb = await openDB();
      const delTx = delDb.transaction(STORE, "readwrite");
      delTx.objectStore(STORE).delete(entry.id);
      await new Promise((r) => { delTx.oncomplete = r; });
      delDb.close();
      processed++;
    } catch {
      failed++;
      break; // stop on first failure (might still be offline)
    }
  }
  return { processed, failed };
}
